import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useAuth } from '../auth/AuthProvider'
import { DataTable } from '../components/DataTable'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketLoadingIndicator } from '../components/MarketLoadingIndicator'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { formatRubles } from '../components/suppliers/minOrderAmount'
import { SupplierCardModal } from '../components/suppliers/SupplierCardModal'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import {
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/restaurantMyPrices'
import { ogrnLabel } from '../utils/ogrn'

/**
 * Карточки внешних поставщиков из «Моих прайсов».
 *
 * До сих пор карточка заводилась сама при разборе прайс-листа и править её было негде:
 * поставщик существовал как значение фильтра. Минимальная сумма заказа — первое, что
 * закупщик знает о поставщике, а система нет, поэтому появилось и место для правки.
 */
export const RestaurantSuppliersScreen = () => {
  const { session } = useAuth()
  const organizationId = session?.organizationId ?? 0

  const [suppliers, setSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<RestaurantPrivateSupplierDto | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const reload = useCallback(async () => {
    if (!organizationId) return
    setLoading(true)
    try {
      setSuppliers(await listRestaurantPrivateSuppliers(organizationId))
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось загрузить поставщиков'))
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void reload()
  }, [reload])

  const sorted = useMemo(
    () =>
      [...suppliers].sort((left, right) =>
        left.legalName.localeCompare(right.legalName, 'ru'),
      ),
    [suppliers],
  )

  if (!session) return null

  return (
    <section className="market-page">
      <MarketPageHeader
        title="Мои поставщики"
        description="Поставщики, чьи прайсы вы загрузили. Минимальная сумма заказа подсказывается в корзине до формирования бланка."
        actions={
          <Link className="echat-btn echat-btn--ghost echat-btn--sm" to="/my-prices">
            К моим прайсам
          </Link>
        }
      />

      {message ? (
        <p className="market-muted" role="status">
          {message}
        </p>
      ) : null}

      {loading ? (
        <MarketLoadingIndicator message="Загружаем поставщиков…" />
      ) : sorted.length === 0 ? (
        <MarketEmptyState
          title="Поставщиков пока нет"
          description="Карточка поставщика заводится при загрузке его прайс-листа в «Мои прайсы»."
        />
      ) : (
        <DataTable caption="Поставщики из «Моих прайсов»">
          <thead>
            <tr>
              <th>Поставщик</th>
              <th>Регистрационный номер</th>
              <th>Минимальная сумма заказа</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((supplier) => (
              <tr
                key={supplier.id}
                className="market-table__row--clickable"
                onClick={() => setEditing(supplier)}
              >
                <td className="market-table__cell--primary" data-label="Поставщик">
                  {/*
                    Показываем юридическое название, а не прежнее отображаемое имя:
                    правится теперь именно оно, и подстановка старой подписи выглядела бы
                    так, будто переименование не сохранилось.
                  */}
                  {/*
                    Строка кликабельна целиком, но сама по себе <tr> не получает фокус с
                    клавиатуры и не видна экранным чтецам. Поэтому название — настоящая
                    кнопка: Tab попадает на неё, Enter открывает карточку. Клик по ней
                    всплывает до строки, и обработчик один.
                  */}
                  <button type="button" className="market-table__row-link">
                    {supplier.legalName}
                  </button>
                </td>
                <td data-label="Регистрационный номер">
                  {supplier.ogrn
                    ? `${ogrnLabel(supplier.ogrn)} ${supplier.ogrn}`
                    : supplier.inn
                      ? `ИНН ${supplier.inn}`
                      : '—'}
                </td>
                <td data-label="Минимальная сумма заказа">
                  {supplier.minOrderAmountUnknown
                    ? 'Неизвестна'
                    : supplier.minOrderAmount != null
                      ? formatRubles(supplier.minOrderAmount)
                      : 'Не указана'}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {editing ? (
        <SupplierCardModal
          supplier={editing}
          onDismiss={() => setEditing(null)}
          onSaved={(updated) => {
            setSuppliers((current) =>
              current.map((entry) => (entry.id === updated.id ? updated : entry)),
            )
            setEditing(null)
          }}
        />
      ) : null}
    </section>
  )
}
